import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Receipt } from 'lucide-react'
import { getInvoices } from '../api/invoice-facteur'
import { Badge } from '../components/Badge'
import { Modal } from '../components/Modal'
import { EmptyState } from '../components/EmptyState'
import { SearchInput } from '../components/SearchInput'
import { Select, SelectItem } from '../components/ui/select'
import { useDateFmt } from '../hooks/useDateFmt'
import type { Invoice, InvoiceStatus } from '../types'

const ALL_STATUSES = '_all'

const STATUS_VARIANT: Record<InvoiceStatus, 'default' | 'success' | 'danger' | 'warning' | 'info'> = {
  pending:         'default',
  client_resolved: 'info',
  client_created:  'info',
  sent:            'success',
  failed:          'danger',
}

const STATUS_LABEL: Record<InvoiceStatus, string> = {
  pending:         'Pending',
  client_resolved: 'Client resolved',
  client_created:  'Client created',
  sent:            'Sent',
  failed:          'Failed',
}

function subtotal(inv: Invoice): number {
  return inv.lineItems.reduce((sum, l) => sum + l.quantity * l.unitPrice, 0)
}

function fmtAmount(n: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n)
  } catch {
    return `${n.toFixed(2)} ${currency}`
  }
}

export function Invoices() {
  const fmt = useDateFmt()
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Invoice | null>(null)

  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['invoices', status],
    queryFn: () => getInvoices({ status: status || undefined, limit: 200 }).then((r) => r.data),
  })

  const filtered = invoices.filter((inv: Invoice) =>
    [inv.invoiceNumber, inv.clientName, inv.pdpProvider].some((v) => v.toLowerCase().includes(search.toLowerCase()))
  )

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Invoices</h1>
          <p className="text-sm text-ink-dim mt-0.5">Invoices processed by Invoice Facteur</p>
        </div>
        <div className="flex items-center gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder="Search invoices" />
          <Select value={status || ALL_STATUSES} onValueChange={(v) => setStatus(v === ALL_STATUSES ? '' : v)} className="w-44">
            <SelectItem value={ALL_STATUSES}>All statuses</SelectItem>
            {(Object.keys(STATUS_LABEL) as InvoiceStatus[]).map((s) => (
              <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>
            ))}
          </Select>
        </div>
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-ink-dim">Loading</div>
        ) : invoices.length === 0 ? (
          <EmptyState icon={Receipt} title="No invoices" description="Invoices appear here once a new-invoice event is processed" />
        ) : filtered.length === 0 ? (
          <EmptyState icon={Receipt} title="No results" description={`No invoices match "${search}"`} />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Client</th>
                <th>Amount</th>
                <th>PDP</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv: Invoice) => (
                <tr
                  key={inv.id}
                  onClick={() => setSelected(inv)}
                  className="cursor-pointer"
                >
                  <td className="font-medium text-ink">{inv.invoiceNumber}</td>
                  <td className="text-ink-dim truncate max-w-xs">{inv.clientName}</td>
                  <td className="font-mono text-xs">{fmtAmount(subtotal(inv), inv.currency)}</td>
                  <td className="text-ink-dim capitalize">{inv.pdpProvider}</td>
                  <td>
                    <Badge variant={STATUS_VARIANT[inv.status]}>{STATUS_LABEL[inv.status]}</Badge>
                  </td>
                  <td className="text-ink-dim">{fmt(inv.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <Modal title={selected.invoiceNumber} onClose={() => setSelected(null)} size="lg">
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <Badge variant={STATUS_VARIANT[selected.status]}>{STATUS_LABEL[selected.status]}</Badge>
              <p className="text-xs text-ink-dim">
                Issued {fmt(selected.issueDate)}{selected.dueDate ? ` · Due ${fmt(selected.dueDate)}` : ''}
              </p>
            </div>

            {selected.status === 'failed' && selected.errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 text-xs text-red-700">
                {selected.errorMessage}
              </div>
            )}

            <div>
              <p className="text-theme-xs font-semibold uppercase tracking-widest text-ink-dim mb-1.5">Client</p>
              <div className="text-sm text-ink space-y-0.5">
                <p className="font-medium">{selected.clientName}</p>
                <p className="text-xs text-ink-dim">
                  {selected.clientRegistrationNumber} · {selected.clientCountryCode}
                  {selected.clientVatNumber ? ` · ${selected.clientVatNumber}` : ''}
                </p>
                {selected.clientEmail && <p className="text-xs text-ink-dim">{selected.clientEmail}</p>}
              </div>
            </div>

            <div>
              <p className="text-theme-xs font-semibold uppercase tracking-widest text-ink-dim mb-1.5">Line items</p>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th className="text-right">Qty</th>
                      <th className="text-right">Unit price</th>
                      <th className="text-right">VAT</th>
                      <th className="text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.lineItems.map((l, i) => (
                      <tr key={i}>
                        <td>{l.description}</td>
                        <td className="text-right">{l.quantity} {l.unit}</td>
                        <td className="text-right font-mono">{fmtAmount(l.unitPrice, selected.currency)}</td>
                        <td className="text-right">{l.vatRate}%</td>
                        <td className="text-right font-mono">{fmtAmount(l.quantity * l.unitPrice, selected.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-right text-xs font-semibold text-ink mt-2">
                Subtotal: {fmtAmount(subtotal(selected), selected.currency)}
              </p>
            </div>

            {selected.payment && (
              <div>
                <p className="text-theme-xs font-semibold uppercase tracking-widest text-ink-dim mb-1.5">Payment</p>
                <p className="text-xs text-ink-dim">
                  {selected.payment.means}
                  {selected.payment.iban ? ` · ${selected.payment.iban}` : ''}
                  {selected.payment.reference ? ` · ref ${selected.payment.reference}` : ''}
                </p>
              </div>
            )}

            <div>
              <p className="text-theme-xs font-semibold uppercase tracking-widest text-ink-dim mb-1.5">PDP</p>
              <p className="text-xs text-ink-dim space-y-0.5">
                <span className="capitalize">{selected.pdpProvider}</span>
                {selected.pdpCompanyId ? ` · company ${selected.pdpCompanyId}` : ''}
                {selected.pdpPeppolId ? ` · peppol ${selected.pdpPeppolId}` : ''}
                {selected.pdpDocumentId ? ` · document ${selected.pdpDocumentId}` : ''}
              </p>
            </div>

            <div>
              <p className="text-theme-xs font-semibold uppercase tracking-widest text-ink-dim mb-1.5">Raw event metadata</p>
              <pre className="bg-paper-sunken border border-paper-border rounded-md px-4 py-3 text-xs font-mono text-ink overflow-x-auto max-h-64 scrollbar-thin">
                {JSON.stringify(selected.rawMetadata, null, 2)}
              </pre>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
